import {
    PWA_PUSH_PROMPT_COOLDOWN_MS,
    PWA_PUSH_PROMPT_DISMISSED_KEY_PREFIX,
    PWA_PUSH_PROMPT_DISMISSED_LEGACY_VALUE,
    PWA_PUSH_SYNCED_KEY_PREFIX,
} from '../constants';

function dismissedKey(userId: string): string {
    return `${PWA_PUSH_PROMPT_DISMISSED_KEY_PREFIX}${userId}`;
}

function syncedKey(userId: string): string {
    return `${PWA_PUSH_SYNCED_KEY_PREFIX}${userId}`;
}

function writeDismissedAt(userId: string, at: number, storage: Storage): void {
    try {
        storage.setItem(dismissedKey(userId), String(at));
    } catch {
        // ignore quota / private mode
    }
}

/**
 * When the prompt was last soft-dismissed, or null if it never was.
 *
 * Anything that is not a usable past timestamp — the legacy "never ask again" `'1'`, a corrupt
 * entry, or a future time after a device clock change — restarts the cooldown instead of reading
 * as long expired, so users who dismissed before this cooldown shipped are not re-prompted at once.
 */
function readDismissedAt(userId: string, storage: Storage, now: number): number | null {
    const raw = storage.getItem(dismissedKey(userId));
    if (raw === null) {
        return null;
    }

    const dismissedAt = Number(raw);
    const isUsablePastTimestamp = Number.isFinite(dismissedAt) && dismissedAt > 0 && dismissedAt <= now;
    if (raw !== PWA_PUSH_PROMPT_DISMISSED_LEGACY_VALUE && isUsablePastTimestamp) {
        return dismissedAt;
    }

    writeDismissedAt(userId, now, storage);
    return now;
}

/** True while a soft dismissal is still inside its cooldown — not "was ever dismissed". */
export function hasDismissedPushPrompt(userId: string, storage: Storage = localStorage, now: number = Date.now()): boolean {
    const dismissedAt = readDismissedAt(userId, storage, now);
    return dismissedAt !== null && now - dismissedAt < PWA_PUSH_PROMPT_COOLDOWN_MS;
}

export function markPushPromptDismissed(userId: string, storage: Storage = localStorage, now: number = Date.now()): void {
    writeDismissedAt(userId, now, storage);
}

export function clearPushPromptDismissed(userId: string, storage: Storage = localStorage): void {
    storage.removeItem(dismissedKey(userId));
}

export function hasSyncedPushSubscription(userId: string, storage: Storage = localStorage): boolean {
    return storage.getItem(syncedKey(userId)) === '1';
}

export function markPushSubscriptionSynced(userId: string, storage: Storage = localStorage): void {
    storage.setItem(syncedKey(userId), '1');
}

export function clearPushSubscriptionSynced(userId: string, storage: Storage = localStorage): void {
    storage.removeItem(syncedKey(userId));
}

export function shouldShowPushPermissionPrompt(params: {
    userId: string | undefined;
    isStandalone: boolean;
    notificationPermission: NotificationPermission | 'unsupported';
    storage?: Storage;
    now?: number;
}): boolean {
    const { userId, isStandalone, notificationPermission, storage = localStorage, now = Date.now() } = params;
    if (!userId || !isStandalone) {
        return false;
    }
    if (notificationPermission !== 'default') {
        return false;
    }
    if (hasDismissedPushPrompt(userId, storage, now)) {
        return false;
    }
    return true;
}
